import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { cn } from 'helpers/classnames';
import type { ProductImage } from 'types/cloudCatalog';
import type { GalleryProductBundle } from 'reducers/detail/productDetail';
import {
  filterProductPhotos,
  getAllSizeChartImages,
  getPDPTrackingPropsFormatted,
  getProductImagesFormatted,
  getProductVideoFormatted,
  SIX_SHOT_PHOTO_PRODUCTS
} from 'helpers/ProductUtils';
import useMartyContext from 'hooks/useMartyContext';
import Grid from 'containers/layout/Grid';
import Image from 'components/common/zoom/Image';
import MelodyCarousel from 'components/common/MelodyCarousel';
import MelodyVideoPlayer from 'components/common/melodyVideo/MelodyVideoPlayer';
import ThumbnailCarousel from 'components/productdetail/productGallery/ThumbnailCarousel';
import type { FeaturedImage, PDPFeaturedVideo } from 'types/product';
import { track } from 'apis/amethyst';
import { evProductImageImpression } from 'events/product';
import { isAssigned, triggerAssignment } from 'actions/ab';
import { HYDRA_BROOKS_VIDEO, HYDRA_SIZE_CHART_CAROUSEL } from 'constants/hydraTests';
import type { AppDispatch } from 'entrypoints/bootstrapOnClient';

import css from 'styles/components/productdetail/productGallery/gallery.scss';

export interface GalleryProps {
  images: ProductImage[];
  product: GalleryProductBundle;
  styleId: string;
  imageChildren?: React.ReactNode;
  isHydraPhotoAngles: boolean;
}

const Gallery = (props: GalleryProps) => {
  const { images, product, styleId, imageChildren, isHydraPhotoAngles } = props;
  const { testId } = useMartyContext();
  const dispatch: AppDispatch = useDispatch();
  const {
    productName,
    brandName,
    productId,
    defaultProductType,
    videos,
    youtubeData: { embedUrl, videoId },
    description
  } = product || {};
  const isHydraBrooksVideo = useSelector(state => isAssigned(HYDRA_BROOKS_VIDEO, 1, state));
  const isHydraSizeChartCarousel = useSelector(state => isAssigned(HYDRA_SIZE_CHART_CAROUSEL, 1, state));
  const isYouTubeVideo = !!videoId;
  const prependedAltText = `${brandName} ${productName}`;

  const productImages: FeaturedImage[] = getProductImagesFormatted(images, defaultProductType) || [];

  const filteredProductImages: FeaturedImage[] = isHydraPhotoAngles
    ? filterProductPhotos(productImages, { productId, styleId }, SIX_SHOT_PHOTO_PRODUCTS)
    : productImages;

  const allSizeChartImages = isHydraSizeChartCarousel ? getAllSizeChartImages(filteredProductImages.length, description) : null;

  const filteredImagesWithSizeCharts = allSizeChartImages ? [...filteredProductImages, ...allSizeChartImages] : filteredProductImages;

  const { length } = filteredProductImages;
  const productVideo: PDPFeaturedVideo | undefined = getProductVideoFormatted(videos, productId, length, isYouTubeVideo, embedUrl);
  const mainImage = length > 0 && filteredProductImages[0];
  const { imageId, type } = mainImage || {};
  const [thumbnailImage, setThumbnailImage]: any = useState(null);

  // 2022-11-11 : display videos for select product/styles (brooks ghost 14, catamount )
  const VIDEO_TEST_PRODUCTS = [
    { productId: '9515355', styleIds: ['5592573', '5411896', '5349098', '5349092', '5349094', '5349097', '5505300'] },
    { productId: '9462684', styleIds: ['5349457', '5228862', '5504313'] }
  ];
  const isVideoTestStyle = VIDEO_TEST_PRODUCTS.some(product => product.productId === productId && product.styleIds.includes(styleId));

  const handleImageZoom = (isZoomed: boolean) => {
    if (!isZoomed) {
      setThumbnailImage(null);
    }
  };

  const onThumbnailSelected = (thumbnailIndex: number) => {
    const asset = filteredImagesWithSizeCharts[thumbnailIndex];
    if (asset) {
      setThumbnailImage(asset);
    }
  };

  const createThumbnailCarousel = (index: number) => (
    <ThumbnailCarousel
      defaultSelectedIndex={index}
      onThumbnailSelected={onThumbnailSelected}
      prependedAltText={prependedAltText}
      assets={filteredImagesWithSizeCharts}
    />
  );

  useEffect(() => {
    track(() => [
      evProductImageImpression,
      {
        productId,
        styleId,
        imageAngles: filteredImagesWithSizeCharts.map(image => image.angleType)
      }
    ]);
  }, [productId, styleId]);

  useEffect(() => {
    if (isVideoTestStyle) {
      dispatch(triggerAssignment(HYDRA_BROOKS_VIDEO));
    }
  }, [triggerAssignment, isVideoTestStyle]);

  return (
    <>
      {mainImage &&
        length === 1 && ( // show just the main image if theres only 1
          <div className={css.imageWrapper} data-test-id={testId('mainProductImage')}>
            <Image
              minHeight={600}
              key={imageId}
              image={mainImage}
              zoomedImageOverride={thumbnailImage}
              showInstructions={false}
              prependedAltText={prependedAltText}
              isOverlayShown={true}
              tracking={getPDPTrackingPropsFormatted('Zoom-In', `${type}-Click`)}
              onZoom={handleImageZoom}
              additionalZoomContent={createThumbnailCarousel(0)}
            />
            {imageChildren}
          </div>
        )}
      {length > 1 && (
        <Grid columns={2}>
          {filteredProductImages.map((asset, index) => {
            const { imageId, type } = asset;

            const isFirstImage = index === 0;

            const showTestVideo = isVideoTestStyle && isHydraBrooksVideo;
            const testVideoInsertionIndex = 2;

            const showImageChildren = !!imageChildren;
            const imageChildrenInsertionIndex = 0;

            return (
              <Fragment key={imageId + index}>
                {showTestVideo && testVideoInsertionIndex === index && (
                  <div className={css.videoWrapper}>
                    <MelodyVideoPlayer
                      index={index}
                      isYouTubeVideo={false}
                      type={'video'}
                      angleType={'VIDEO'}
                      alt={'View product style video'}
                      slotDetails={{
                        productId: productId,
                        autoplay: true,
                        componentName: 'DESKTOP_PDP_GRID_VIDEO',
                        src: `https://www.zappos.com/marty-assets/short-form-videos/${productId}/${styleId}.mp4`
                      }}
                      isAutoplaying={true}
                      isFullWidth={false}
                      trackInView
                    />
                  </div>
                )}

                <div
                  className={cn(css.imageWrapper, { [css.twoColumnImage]: isFirstImage })}
                  data-test-id={isFirstImage ? testId('mainProductImage') : undefined}
                >
                  <Image
                    minHeight={550}
                    image={asset}
                    zoomedImageOverride={thumbnailImage}
                    showInstructions={false}
                    prependedAltText={prependedAltText}
                    isOverlayShown={true}
                    tracking={getPDPTrackingPropsFormatted('Zoom-In', `${type}-Click`)}
                    onZoom={handleImageZoom}
                    additionalZoomContent={createThumbnailCarousel(index)}
                  />
                  {showImageChildren && imageChildrenInsertionIndex === index && imageChildren}{' '}
                  {/* If we are on the 2nd image show the reco drawer / image children */}
                </div>
              </Fragment>
            );
          })}
          {isHydraSizeChartCarousel && allSizeChartImages && (
            <MelodyCarousel classes={{ nextButton: css.carouselNextBtn, prevButton: css.carouselPrevBtn }} isFullWidth={true} reconfigure={true}>
              {allSizeChartImages.map((chartImage, index) => (
                <Image
                  key={chartImage.imageId}
                  buttonClass={css.imageCarouselBtn}
                  minHeight={550}
                  image={chartImage}
                  zoomedImageOverride={thumbnailImage}
                  showInstructions={false}
                  isOverlayShown={true}
                  tracking={getPDPTrackingPropsFormatted('Zoom-In', `${type}-Click`)}
                  onZoom={handleImageZoom}
                  additionalZoomContent={createThumbnailCarousel(filteredProductImages.length + index)}
                />
              ))}
            </MelodyCarousel>
          )}
        </Grid>
      )}
      {productVideo && <MelodyVideoPlayer {...productVideo} isAutoplaying={false} isFullWidth={true} trackInView />}
    </>
  );
};

export default Gallery;
